import { copyBytes } from "./bytes.js";
import type { Endpoint } from "./endpoint.js";
import { BlindBindError } from "./errors.js";
import {
  FrameType,
  MAX_PLAINTEXT_FRAME_BYTES,
  decodeEncryptedFrame,
  decryptFramePayload,
  encryptFramePayload,
} from "./protocol.js";
import type { HandshakeResult } from "./handshake.js";
import type { SocketInbox, WebSocketLike } from "./websocket.js";

export interface BlindCloseInfo {
  code: number;
  reason: string;
  clean: boolean;
}

export interface BlindConnection {
  readonly sessionId: string;
  readonly endpoint: Endpoint;
  readonly state: "open" | "closed";
  readonly readable: ReadableStream<Uint8Array>;
  readonly writable: WritableStream<Uint8Array>;
  readonly closed: Promise<BlindCloseInfo>;

  close(options?: { code?: number; reason?: string }): void;
}

export function createBlindConnection(
  socket: WebSocketLike,
  inbox: SocketInbox,
  endpoint: Endpoint,
  handshake: HandshakeResult,
): BlindConnection {
  if (socket.readyState !== 1) {
    inbox.detach();
    throw new BlindBindError("connection_failed", "WebSocket closed as the encrypted handshake completed");
  }
  return new BlindConnectionImpl(socket, inbox, endpoint, handshake);
}

class BlindConnectionImpl implements BlindConnection {
  readonly sessionId: string;
  readonly endpoint: Endpoint;
  readonly readable: ReadableStream<Uint8Array>;
  readonly writable: WritableStream<Uint8Array>;
  readonly closed: Promise<BlindCloseInfo>;

  readonly #socket: WebSocketLike;
  readonly #inbox: SocketInbox;
  readonly #sendKey: CryptoKey;
  readonly #receiveKey: CryptoKey;
  #sendSequence = 1n;
  #receiveSequence = 1n;
  #state: "open" | "closed" = "open";
  #resolveClosed: (info: BlindCloseInfo) => void = () => undefined;
  #readController: ReadableStreamDefaultController<Uint8Array> | null = null;
  #reading = false;
  #readFinished = false;
  #outerClose: BlindCloseInfo | null = null;

  constructor(socket: WebSocketLike, inbox: SocketInbox, endpoint: Endpoint, handshake: HandshakeResult) {
    this.#socket = socket;
    this.#inbox = inbox;
    this.endpoint = endpoint;
    this.sessionId = handshake.sessionId;
    this.#sendKey = handshake.sendKey;
    this.#receiveKey = handshake.receiveKey;
    this.closed = new Promise((resolve) => {
      this.#resolveClosed = resolve;
    });
    this.readable = new ReadableStream<Uint8Array>({
      start: (controller) => {
        this.#readController = controller;
      },
      pull: () => this.#readOne(),
      cancel: () => this.close(),
    });
    this.writable = new WritableStream<Uint8Array>({
      write: (chunk) => this.#write(chunk),
      close: () => this.close(),
      abort: () => this.close(),
    });
    socket.addEventListener("close", this.#onClose);
  }

  get state(): "open" | "closed" {
    return this.#state;
  }

  close(options: { code?: number; reason?: string } = {}): void {
    if (this.#state === "closed") return;
    this.#socket.close(options.code ?? 1000, options.reason ?? "");
  }

  async #write(chunk: Uint8Array): Promise<void> {
    if (!(chunk instanceof Uint8Array)) throw new TypeError("Blind connection writes must be Uint8Array values");
    if (this.#state !== "open") throw new BlindBindError("closed", "Blind connection is closed");
    for (let offset = 0; offset < chunk.byteLength; offset += MAX_PLAINTEXT_FRAME_BYTES) {
      const plaintext = copyBytes(
        chunk.subarray(offset, Math.min(offset + MAX_PLAINTEXT_FRAME_BYTES, chunk.byteLength)),
      );
      await this.#waitForSocketCapacity();
      if (this.#state !== "open") throw new BlindBindError("closed", "Blind connection closed during write");
      const sequence = this.#sendSequence;
      this.#sendSequence += 1n;
      this.#socket.send(await encryptFramePayload(this.#sendKey, FrameType.Data, sequence, plaintext));
    }
  }

  async #readOne(): Promise<void> {
    if (this.#reading || this.#readFinished) return;
    this.#reading = true;
    try {
      const frame = decodeEncryptedFrame(await this.#inbox.nextBytes(), FrameType.Data);
      if (frame.sequence !== this.#receiveSequence) {
        throw new BlindBindError(
          "protocol_error",
          `Expected frame ${this.#receiveSequence}, received ${frame.sequence}`,
        );
      }
      this.#receiveSequence += 1n;
      this.#readController?.enqueue(await decryptFramePayload(this.#receiveKey, frame));
    } catch (error) {
      if (this.#outerClose) {
        this.#finishReadable();
        return;
      }
      const normalized =
        error instanceof BlindBindError
          ? error
          : new BlindBindError("protocol_error", "Blind connection read failed", { cause: error });
      this.#readFinished = true;
      this.#inbox.detach();
      this.#readController?.error(normalized);
      this.#socket.close(4400, normalized.message.slice(0, 120));
    } finally {
      this.#reading = false;
      if (this.#outerClose && !this.#readFinished) queueMicrotask(() => void this.#readOne());
    }
  }

  async #waitForSocketCapacity(): Promise<void> {
    while (this.#socket.bufferedAmount > 1 << 20) {
      if (this.#state !== "open") throw new BlindBindError("closed", "Blind connection is closed");
      await new Promise((resolve) => setTimeout(resolve, 4));
    }
  }

  readonly #onClose: EventListener = (event) => {
    if (this.#state === "closed") return;
    this.#state = "closed";
    this.#socket.removeEventListener("close", this.#onClose);
    const close = event instanceof CloseEvent ? event : null;
    const info = { code: close?.code ?? 1006, reason: close?.reason ?? "", clean: close?.wasClean ?? false };
    this.#outerClose = info;
    this.#resolveClosed(info);
    void this.#readOne();
  };

  #finishReadable(): void {
    if (this.#readFinished) return;
    this.#readFinished = true;
    this.#inbox.detach();
    try {
      this.#readController?.close();
    } catch {
      // A prior cancellation or authenticated-frame error already terminated the stream.
    }
  }
}
